import { useState, useEffect, useCallback } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import { User, AuthState } from '../types';
import { getOrCreateUser } from '../lib/supabase';

export function useAuth(): AuthState & {
  login: () => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
} {
  const { ready, authenticated, user: privyUser, login: privyLogin, logout: privyLogout } = usePrivy();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadUser = useCallback(async () => {
    if (!authenticated || !privyUser) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    try {
      const email = privyUser.email?.address || privyUser.google?.email || '';
      const name = privyUser.google?.name || email.split('@')[0] || 'User';

      const dbUser = await getOrCreateUser(privyUser.id, name, email);
      setUser(dbUser);
    } catch (error) {
      console.error('Error loading user:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, [authenticated, privyUser]);

  useEffect(() => {
    if (ready) {
      loadUser();
    }
  }, [ready, authenticated, loadUser]);

  const logout = async () => {
    await privyLogout();
    setUser(null);
  };

  return {
    user,
    isLoading: !ready || isLoading,
    isAuthenticated: authenticated && !!user,
    login: privyLogin,
    logout,
    refreshUser: loadUser,
  };
}
